import {
  mergeClasses,
  SearchBox,
  SearchBoxChangeEvent,
  Title3,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowUp16Regular,
  ChatMultiple24Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import {
  addFavoriteConversation,
  createConversation,
  deleteConversation,
  getConversationsWithCount,
  getMessagesOfConversation,
  getMyWorkspaces,
  getUsers,
  removeFavoriteConversation,
  trackConversationClick,
  trackConversationCopy,
} from "api";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { CopyOption } from "models/enums/CopyType";
import { IConversation } from "models/IConversation";
import { ICopyOption } from "models/ICopyOption";
import { ICopyResult } from "components/conversations/copyOptionsDialog/CopyOptionsDialog.types";
import { IMessage } from "models/IMessage";
import { IUser } from "models/IUser";
import { IWorkspace } from "models/IWorkspace";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { useSharedBannerStyles } from "theme/sharedStyles";
import { setActiveWorkspace } from "../../redux/activeWorkspaceSlice";
import { setCurrentConversation } from "../../redux/conversations";
import { setCurrentMessages } from "../../redux/messages";
import { setCurrentPrompt } from "../../redux/selectedPrompt";
import { setTagsForKey } from "../../redux/tags";
import { showToast } from "../../redux/toastActions";
import { setUsers } from "../../redux/users";
import { clearLibraryDocumentsForKey } from "../../redux/workspaceLibraryDocuments";
import {
  selectAvailableWorkspaces,
  setWorkspaces,
} from "../../redux/workspacesSlice";
import { ChatFilters } from "./chatFilters/ChatFilters";
import { ChatListItem } from "./chatListItem/ChatListItem";
import { useMyChatsStyles } from "./MyChats.styles";

const PAGE_SIZE = 10;
const SEARCH_CACHE_TIME = 60_000;
const DEFAULT_CACHE_TIME = 300_000;
const STALE_TIME = 30_000;
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;

export const MyChats = (): JSX.Element => {
  const styles = useMyChatsStyles();
  const bannerStyles = useSharedBannerStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const deleteTriggeredByRef = useRef<HTMLElement | null>(null);
  const wasDeleteDialogOpen = useRef<boolean>(false);
  const [selectedConversation, setSelectedConversation] =
    useState<IConversation | null>(null);
  const [conversationToDelete, setConversationToDelete] =
    useState<IConversation | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [favoritingConversationId, setFavoritingConversationId] = useState<
    string | undefined
  >(undefined);

  const [filterWorkspaceId, setFilterWorkspaceId] = useState<
    string | undefined
  >(undefined);
  const [filterIsFavorited, setFilterIsFavorited] = useState<boolean>(false);
  const [filterIsAscending, setFilterIsAscending] = useState<boolean>(false);
  const [filterSortField, setFilterSortField] = useState<"name" | "updatedAt">(
    "updatedAt",
  );
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
  const [accessLostMessage, setAccessLostMessage] = useState<string | null>(
    null,
  );

  const currentUser: IUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const workspaces: IWorkspace[] = useSelector(selectAvailableWorkspaces);
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const data: IWorkspace[] = await getMyWorkspaces();
        dispatch(setWorkspaces(data));
        const personalWorkspace: IWorkspace | undefined = data.find(
          (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
        );

        if (!activeWorkspace && personalWorkspace) {
          dispatch(setActiveWorkspace(personalWorkspace));
        }
      } catch (error) {
        console.error("Failed to fetch workspaces:", error);
      }
    };
    fetchWorkspaces();
  }, [dispatch, activeWorkspace]);

  useEffect(() => {
    if (workspaces.length > 0 && filterWorkspaceId !== undefined) {
      const exists = workspaces.some((w) => w.id === filterWorkspaceId);
      if (!exists) {
        setFilterWorkspaceId(undefined);
      }
    }
  }, [workspaces, filterWorkspaceId]);

  useEffect(() => {
    if (!users.length) {
      const fetchUsers = async () => {
        try {
          const data = await getUsers();
          dispatch(setUsers(data));
        } catch (error) {
          console.error("Failed to fetch users:", error);
        }
      };
      fetchUsers();
    }
  }, [dispatch, users.length]);

  const personalWorkspaceId: string | undefined = useMemo(
    () =>
      workspaces.find((w) => w.name === ConstantValues.PERSONAL_WORKSPACE)?.id,
    [workspaces],
  );
  const workspaceNameMap = useMemo(
    () => Object.fromEntries(workspaces.map((w) => [w.id, w.name])),
    [workspaces],
  );
  const workspaceId =
    activeWorkspace?.id ?? personalWorkspaceId ?? ConstantValues.EMPTY_STRING;

  const handleFilterChange = useCallback(async () => {
    setIsFilterLoading(true);
    setTimeout(() => setIsFilterLoading(false), 300);
  }, []);

  const handleSearchChange = useCallback(
    (_e: SearchBoxChangeEvent, data: { value: string }) => {
      const value = data.value;
      setSearchText(value);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        setDebouncedSearchText(ConstantValues.EMPTY_STRING);
      } else if (trimmed.length >= MIN_SEARCH_LENGTH) {
        searchDebounceRef.current = setTimeout(() => {
          setDebouncedSearchText(trimmed);
        }, DEBOUNCE_DELAY);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const apiWorkspaceId = filterWorkspaceId;

  const {
    data: conversationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isPreviousData,
  } = useInfiniteQuery(
    [
      "conversations",
      apiWorkspaceId,
      filterIsFavorited,
      filterIsAscending,
      debouncedSearchText,
    ],
    async ({ pageParam = 0 }) => {
      return await getConversationsWithCount({
        workspaceId:
          apiWorkspaceId === ConstantValues.EMPTY_STRING
            ? undefined
            : apiWorkspaceId,
        favorited: filterIsFavorited,
        ascending: filterIsAscending,
        searchText: debouncedSearchText || undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
      });
    },
    {
      enabled: apiWorkspaceId === undefined || Boolean(apiWorkspaceId),
      keepPreviousData: true,
      staleTime: STALE_TIME,
      cacheTime: debouncedSearchText ? SEARCH_CACHE_TIME : DEFAULT_CACHE_TIME,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        if (
          !lastPage?.conversations ||
          lastPage.conversations.length < PAGE_SIZE
        ) {
          return undefined;
        }
        const nextOffset = pages.length * PAGE_SIZE;
        return nextOffset;
      },
      onError: (err: any) => {
        console.error("getConversationsWithCount failed:", err);
        if (err?.status === 403) {
          setAccessLostMessage(
            "You no longer have access to this workspace. Switch to All Workspaces to see your available chats.",
          );
        }
      },
    },
  );

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;

    setShowBackToTop(scrollTop > 200);

    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (conversationToDelete) {
      wasDeleteDialogOpen.current = true;
    } else if (wasDeleteDialogOpen.current) {
      wasDeleteDialogOpen.current = false;
      if (deleteTriggeredByRef.current) {
        deleteTriggeredByRef.current.focus();
        deleteTriggeredByRef.current = null;
      } else if (scrollContainerRef.current) {
        scrollContainerRef.current.focus();
      }
    }
  }, [conversationToDelete]);

  const handleSelect = useCallback(
    (conversation: IConversation) => {
      if (!conversation.id || !conversation.workspaceId) return;
      setSelectedConversation(conversation);

      localStorage.setItem(
        ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
        conversation.id,
      );
      localStorage.setItem(
        ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
        conversation.workspaceId,
      );

      navigate(
        `/?workspaceId=${encodeURIComponent(conversation.workspaceId)}&conversationId=${encodeURIComponent(conversation.id)}`,
        { state: { conversation } },
      );
      trackConversationClick({ conversationId: conversation.id }).catch((e) => {
        console.log(e);
      });
    },
    [navigate],
  );

  const handleFavorite = useCallback(
    async (conversation: IConversation) => {
      if (!currentUser?.id) return;
      setFavoritingConversationId(conversation.id);
      const isFavorited = conversation.isFavorited;
      try {
        if (isFavorited) {
          await removeFavoriteConversation({ conversationId: conversation.id });
        } else {
          await addFavoriteConversation({ conversationId: conversation.id });
        }

        await queryClient.invalidateQueries(["conversations"]);
      } catch (error) {
        console.error("Failed to update favorite:", error);
        dispatch(showToast("Failed to update favorite status", "error"));
      } finally {
        setFavoritingConversationId(undefined);
      }
    },
    [queryClient, dispatch, currentUser?.id],
  );

  const handleDelete = useCallback((conversation: IConversation) => {
    deleteTriggeredByRef.current = document.activeElement as HTMLElement;
    setConversationToDelete(conversation);
  }, []);

  const confirmDelete = async () => {
    if (!conversationToDelete) return;
    setIsDeleting(true);
    try {
      await deleteConversation({ conversationId: conversationToDelete.id });
      if (selectedConversation?.id === conversationToDelete.id) {
        setSelectedConversation(null);
      }
      queryClient.invalidateQueries(["conversations"]);
    } catch (error: any) {
      console.error("Failed to delete chat:", error);
      if (error?.status === 403 || error?.message?.includes("403")) {
        setAccessLostMessage(
          "You no longer have access to this workspace. Switch to All Workspaces to see your available chats.",
        );
      } else {
        dispatch(showToast("Failed to delete chat", "error"));
      }
    } finally {
      setIsDeleting(false);
      setConversationToDelete(null);
      if (deleteTriggeredByRef.current) {
        deleteTriggeredByRef.current.focus();
      }
    }
  };

  const handleConversationUpdate = useCallback(() => {
    queryClient.invalidateQueries(["conversations"]);
  }, [queryClient]);

  const copyConversation = useCallback(
    async (
      conversation: IConversation,
      copyOption: ICopyOption,
    ): Promise<ICopyResult[]> => {
      try {
        let messages: IMessage[] = [];
        try {
          messages = await getMessagesOfConversation({
            conversationId: conversation.id,
          });
        } catch (error) {
          console.error("Failed to fetch messages for copy:", error);
          messages = [];
        }

        const inputMessages = [...messages]
          .sort((a, b) => a.createdAt - b.createdAt)
          .map((message: IMessage) => ({
            content: message?.content,
            role: message?.role,
            tags: message?.tags,
            files: message?.files,
            sourceDocuments: message?.sourceDocuments,
            llmConfig: message?.llmConfig,
            userId: message?.userId,
            displayName: message?.displayName,
            createdAt: message?.createdAt ? message.createdAt : undefined,
          }));

        const baseConversation = {
          messages: inputMessages,
          title: conversation.title,
        };

        let results: ICopyResult[] = [];

        switch (copyOption.copyOption) {
          case CopyOption.Users: {
            const personalWorkspace = workspaces.find(
              (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
            );
            results = await Promise.all(
              copyOption.selectedIds.map(async (userId) => {
                const createdConversation = await createConversation({
                  conversation: {
                    ...baseConversation,
                    workspaceId:
                      personalWorkspace?.id ?? ConstantValues.EMPTY_STRING,
                    userId,
                    displayName:
                      users.find((user) => user.id === userId)?.displayName ??
                      ConstantValues.EMPTY_STRING,
                    copiedFromUser: copyOption.copiedFromUser,
                    copiedFromConversationId:
                      copyOption.copiedFromConversationId,
                  },
                });
                trackConversationCopy({
                  conversationId: createdConversation.id,
                }).catch((e) => {
                  console.log(e);
                });
                return {
                  recipientId: userId,
                  conversationId: createdConversation.id,
                  workspaceId:
                    personalWorkspace?.id ?? ConstantValues.EMPTY_STRING,
                };
              }),
            );
            break;
          }

          case CopyOption.Workspace:
          case CopyOption.NewWorkspace: {
            results = await Promise.all(
              copyOption.selectedIds.map(async (wsId) => {
                const createdConversation = await createConversation({
                  conversation: {
                    ...baseConversation,
                    workspaceId: wsId,
                    copiedFromUser: copyOption.copiedFromUser,
                    copiedFromConversationId:
                      copyOption.copiedFromConversationId,
                  },
                });
                trackConversationCopy({
                  conversationId: createdConversation.id,
                }).catch((e) => {
                  console.log(e);
                });
                return {
                  recipientId: wsId,
                  conversationId: createdConversation.id,
                  workspaceId: wsId,
                };
              }),
            );
            break;
          }

          case CopyOption.PersonalWorkspace: {
            const personalWorkspace = workspaces.find(
              (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
            );
            const createdConversation = await createConversation({
              conversation: {
                ...baseConversation,
                workspaceId:
                  personalWorkspace?.id ?? ConstantValues.EMPTY_STRING,
                userId: currentUser.id,
                displayName:
                  currentUser.displayName ?? ConstantValues.EMPTY_STRING,
                copiedFromUser: copyOption.copiedFromUser,
                copiedFromConversationId: copyOption.copiedFromConversationId,
              },
            });
            trackConversationCopy({
              conversationId: createdConversation.id,
            }).catch((e) => {
              console.log(e);
            });
            break;
          }

          default:
            console.warn("Unsupported copy option:", copyOption.copyOption);
        }

        queryClient.invalidateQueries(["conversations"]);
        return results;
      } catch (error) {
        console.error("Failed to share chat:", error);
        dispatch(showToast("Failed to share chat", "error"));
        return [];
      }
    },
    [workspaces, users, currentUser, queryClient, dispatch],
  );

  const newChatTargetWorkspace = useMemo(() => {
    if (filterWorkspaceId) {
      return workspaces.find((w) => w.id === filterWorkspaceId);
    }
    return workspaces.find((w) => w.name === ConstantValues.PERSONAL_WORKSPACE);
  }, [filterWorkspaceId, workspaces]);

  const newChatTargetWorkspaceId =
    newChatTargetWorkspace?.id ?? personalWorkspaceId ?? workspaceId;
  const newChatTargetWorkspaceName =
    newChatTargetWorkspace?.name ?? ConstantValues.PERSONAL_WORKSPACE;

  const handleNewChat = useCallback(() => {
    localStorage.removeItem(ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID);
    localStorage.setItem(
      ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
      newChatTargetWorkspaceId,
    );
    dispatch(setCurrentMessages([]));
    dispatch(setCurrentConversation(null));
    dispatch(setTagsForKey({ key: ConstantValues.CHAT, tags: [] }));
    dispatch(setCurrentPrompt({ key: ConstantValues.CHAT, prompt: null }));
    dispatch(clearLibraryDocumentsForKey(ConstantValues.CHAT));
    if (newChatTargetWorkspace) {
      dispatch(setActiveWorkspace(newChatTargetWorkspace));
    }
    navigate(`/?workspaceId=${encodeURIComponent(newChatTargetWorkspaceId)}`);
  }, [dispatch, navigate, newChatTargetWorkspaceId, newChatTargetWorkspace]);

  const handleWorkspaceChange = useCallback(
    (workspaceId: string | undefined) => {
      setFilterWorkspaceId(workspaceId);
      setFilterIsFavorited(false);
      handleFilterChange();
    },
    [handleFilterChange],
  );

  const handleFavoritedChange = useCallback(
    (isFavorited: boolean) => {
      setFilterIsFavorited(isFavorited);
      handleFilterChange();
    },
    [handleFilterChange],
  );

  const handleSortChange = useCallback(
    (isAscending: boolean) => {
      setFilterIsAscending(isAscending);
      handleFilterChange();
    },
    [handleFilterChange],
  );

  const handleSortFieldChange = useCallback((field: "name" | "updatedAt") => {
    setFilterSortField(field);
  }, []);

  const conversations = useMemo(() => {
    const items =
      conversationsData?.pages.flatMap((page) => page.conversations || []) ??
      [];
    if (filterSortField === "name") {
      return [...items].sort((a, b) => {
        const cmp = a.title.localeCompare(b.title);
        return filterIsAscending ? cmp : -cmp;
      });
    }
    return items;
  }, [conversationsData, filterSortField, filterIsAscending]);
  const totalCount = conversationsData?.pages[0]?.total || conversations.length;

  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <div className={styles.header}>
          <Title3 className={styles.headerTitle}>Chats</Title3>
        </div>

        <SearchBox
          className={styles.searchInput}
          appearance="filled-lighter"
          size="large"
          placeholder="Search your chats..."
          value={searchText}
          onChange={handleSearchChange}
          dismiss={searchText ? undefined : null}
          aria-label="Search chats"
        />

        {!isLoading && !isError && conversationsData && (
          <ChatFilters
            workspaces={workspaces}
            selectedWorkspaceId={filterWorkspaceId}
            onWorkspaceChange={handleWorkspaceChange}
            isFavorited={filterIsFavorited}
            onFavoritedChange={handleFavoritedChange}
            isAscending={filterIsAscending}
            onSortChange={handleSortChange}
            sortField={filterSortField}
            onSortFieldChange={handleSortFieldChange}
            conversationCount={totalCount}
            isLoading={isFilterLoading || isFetchingNextPage}
            onNewChat={handleNewChat}
            newChatTargetWorkspaceName={newChatTargetWorkspaceName}
          />
        )}

        {accessLostMessage && (
          <div className={bannerStyles.warningBanner} role="alert">
            <Warning20Regular aria-hidden="true" />
            <span className={bannerStyles.warningBannerText}>
              {accessLostMessage}
            </span>
            <button
              className={bannerStyles.warningBannerAction}
              onClick={() => {
                setAccessLostMessage(null);
                setFilterWorkspaceId(undefined);
                queryClient.invalidateQueries(["conversations"]);
              }}
            >
              Show All Workspaces
            </button>
          </div>
        )}

        {(!conversationsData || isLoading || isPreviousData || isDeleting) &&
          !isError && (
            <div
              className={styles.content}
              role="status"
              aria-label="Loading chats"
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={styles.skeletonItem} aria-hidden="true">
                  <div className={styles.skeletonIcon} />
                  <div className={styles.skeletonContent}>
                    <div className={styles.skeletonTitle} />
                    <div className={styles.skeletonMeta} />
                  </div>
                </div>
              ))}
            </div>
          )}

        {isError && (
          <div className={styles.errorContainer}>
            Failed to load chats. Please try again.
          </div>
        )}

        {!isLoading &&
          !isPreviousData &&
          !isDeleting &&
          !isError &&
          conversationsData && (
            <div className={styles.contentWrapper}>
              <div
                ref={scrollContainerRef}
                className={styles.content}
                tabIndex={-1}
              >
                {conversations.length === 0 && (
                  <div className={styles.emptyState}>
                    <ChatMultiple24Regular />
                    {debouncedSearchText ? (
                      <>
                        <span>
                          No results for &quot;{debouncedSearchText}&quot;
                        </span>
                        <span>Try a different search term</span>
                      </>
                    ) : (
                      <>
                        <span>No chats yet</span>
                        <span>Start a new chat to see it here</span>
                      </>
                    )}
                  </div>
                )}
                {conversations.map((conversation) => (
                  <ChatListItem
                    key={conversation.id}
                    conversation={conversation}
                    currentUser={currentUser}
                    isSelected={selectedConversation?.id === conversation.id}
                    isLoading={favoritingConversationId === conversation.id}
                    searchText={debouncedSearchText}
                    workspaceName={
                      filterWorkspaceId
                        ? undefined
                        : workspaceNameMap[conversation.workspaceId]
                    }
                    onSelect={handleSelect}
                    onFavorite={handleFavorite}
                    onDelete={handleDelete}
                    onRename={handleConversationUpdate}
                    onMove={handleConversationUpdate}
                    onCopy={copyConversation}
                  />
                ))}
                {isFetchingNextPage && (
                  <div className={styles.loadingMore}>Loading more...</div>
                )}
              </div>
              <Tooltip
                content="Back to top"
                relationship="label"
                showDelay={300}
                hideDelay={100}
              >
                <button
                  className={mergeClasses(
                    styles.backToTopButton,
                    showBackToTop && styles.backToTopVisible,
                  )}
                  onClick={scrollToTop}
                  aria-label="Scroll to top"
                  tabIndex={showBackToTop ? 0 : -1}
                >
                  <ArrowUp16Regular />
                </button>
              </Tooltip>
            </div>
          )}
      </div>

      <ActionDialog
        isVisible={!!conversationToDelete}
        title="Delete Chat"
        message={`Are you sure you want to delete "${conversationToDelete?.title ?? ConstantValues.EMPTY_STRING}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={confirmDelete}
        onDismiss={() => setConversationToDelete(null)}
        hasSpinner={isDeleting}
        spinnerMessage="Deleting chat..."
      />
    </div>
  );
};
